import styled from 'styled-components';
import { avatarGradient } from '@/theme/presets/identityGradients';
import { isHttpUrl } from '@/core/lib/isHttpUrl';

export interface AvatarProps {
  name: string;
  size?: number;
  /** Optional identity gradient key (av-grad, av-1..5) OR an uploaded image URL. */
  avatarKey?: string;
}

/* Flat identity colour, no gradient and no halo. The 135deg accent gradient
   plus elevation.glow used to repeat at every avatar-shaped hole in the app —
   the rail chip, the topbar, the composer, every author row, every quote embed
   and every suggestion row — which is most of why one accent read as a wash
   over the whole screen. Depth on an avatar is not a thing that needs saying. */
const Box = styled.span<{ $size: number }>`
  display: inline-grid;
  place-items: center;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.color.surfaceRaised};
  color: #fff;
  font-family: ${({ theme }) => theme.font.display};
  font-weight: 700;
  font-size: ${({ $size }) => Math.round($size * 0.42)}px;
`;

const Img = styled.img<{ $size: number }>`
  display: block;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.sm};
`;

export function Avatar({ name, size = 40, avatarKey }: AvatarProps) {
  if (isHttpUrl(avatarKey)) {
    return <Img $size={size} src={avatarKey} alt={name} role="img" aria-label={name} />;
  }
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  const style = { background: avatarGradient(avatarKey ?? 'av-grad') };
  return (
    <Box $size={size} role="img" aria-label={name} style={style}>
      {initial}
    </Box>
  );
}
